import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import useCcmLicenseStore from '../stores/ccmLicenseStore';
import {
  Shield,
  DoorOpen,
  DoorClosed,
  Activity,
  History,
  Radio,
  Unlock,
  Lock,
  LockOpen,
  AlertTriangle,
  RefreshCw,
  Filter,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Wifi,
  WifiOff,
  Settings,
  Download,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import accessControlTutorial from '../components/tutorials/configs/accessControlTutorial';

// ===========================
// Vendor config map
// ===========================
const VENDOR_CONFIG = {
  FEENICS_ACCESS: { bg: 'rgba(16, 185, 129, 0.15)', text: '#10b981', label: 'Feenics', logo: '/logos/feenics.png' },
  GENETEC_ACS:    { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6', label: 'Genetec', logo: '/logos/genetec.png' },
  AXIS_VAPIX:     { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b', label: 'Axis', logo: '/logos/axis.png' },
};

function getVendorConfig(integrationType) {
  return VENDOR_CONFIG[integrationType] || { bg: 'rgba(107, 114, 128, 0.15)', text: '#6b7280', label: integrationType || 'Unknown', logo: null };
}

// ===========================
// Vendor badge with logo + text fallback
// ===========================
function VendorBadge({ integrationType }) {
  const config = getVendorConfig(integrationType);
  return (
    <span
      className="acs-vendor-badge"
      style={{ background: config.bg, color: config.text }}
    >
      {config.label}
    </span>
  );
}

// ===========================
// Normalize door from any adapter
// ===========================
function normalizeDoor(door) {
  const raw = door.raw || {};

  const mode = door.doorMode || raw.Mode || raw.CurrentMode || null;
  const isLocked = door.locked !== undefined ? door.locked : (mode && mode.toLowerCase() === 'locked');
  const isHeldOpen = door.heldOpen !== undefined ? door.heldOpen
    : (mode && mode.toLowerCase() === 'unlocked') || raw.DoorHeldOpen === true;
  const isOnline = door.online !== undefined ? door.online : true;
  const isForcedOpen = raw.DoorForcedOpen === true;
  const controllerName = raw.ControllerCommonName || raw.ControllerName || door.description || '';

  let displayStatus = 'normal';
  if (!isOnline) displayStatus = 'offline';
  else if (isHeldOpen) displayStatus = 'held-open';
  else if (isLocked) displayStatus = 'locked';
  else if (door.status === 'open' || door.open) displayStatus = 'open';
  else displayStatus = 'normal';

  return {
    ...door,
    mode,
    isLocked,
    isHeldOpen,
    isOnline,
    isForcedOpen,
    controllerName,
    displayStatus,
  };
}

function getModeColor(displayStatus) {
  switch (displayStatus) {
    case 'held-open': return '#ef4444';
    case 'locked': return '#22c55e';
    case 'open': return '#10b981';
    case 'offline': return '#6b7280';
    default: return '#3b82f6';
  }
}

function getModeLabel(door) {
  if (!door.isOnline) return 'Offline';
  if (door.isHeldOpen) return 'Held Open';
  if (door.isLocked) return 'Locked';
  if (door.mode) return door.mode;
  if (door.status === 'open' || door.open) return 'Open';
  return 'Normal';
}

// ===========================
// Component
// ===========================

const AccessControl = () => {
  const { tenantVersion } = useAuthStore();
  const { license: ccmLicense } = useCcmLicenseStore();
  const ccmReadOnly = !!(ccmLicense && ccmLicense.isExpired);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return (t === 'live' || t === 'history' || t === 'readers') ? t : 'readers';
  });

  // Respond to tab changes pushed via URL (e.g. dashboard links to /access?tab=live)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'live' || t === 'history' || t === 'readers') {
      setActiveTab(t);
    }
  }, [searchParams]);

  const [liveEvents, setLiveEvents] = useState([]);
  const [doors, setDoors] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [fetchErrors, setFetchErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [noIntegrations, setNoIntegrations] = useState(false);
  const [doorFilter, setDoorFilter] = useState('');
  const [integrationFilter, setIntegrationFilter] = useState('');
  const [lockdownActive, setLockdownActive] = useState(false);
  const [showLockdownConfirm, setShowLockdownConfirm] = useState(false);
  const [lockdownStep, setLockdownStep] = useState(0);

  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(accessControlTutorial);

  const pollIntervalRef = useRef(null);

  // Reset on tenant change
  useEffect(() => {
    setDoors([]);
    setLiveEvents([]);
    setIntegrations([]);
    setFetchErrors([]);
    setNoIntegrations(false);
    setLoading(true);
  }, [tenantVersion]);

  useEffect(() => {
    loadData();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [tenantVersion]);

  const loadData = useCallback(async () => {
    try {
      const [doorsRes, eventsRes] = await Promise.all([
        fetchWithTenant('/api/acs/doors').then(r => r.json()),
        fetchWithTenant('/api/acs/events?count=50').then(r => r.json()).catch(() => ({ events: [] })),
      ]);

      const doorList = (doorsRes.doors || []).map(normalizeDoor);
      const intList = doorsRes.integrations || [];
      const errList = doorsRes.errors || [];

      setDoors(doorList);
      setIntegrations(intList);
      setFetchErrors(errList);
      setNoIntegrations(intList.length === 0 && errList.length === 0);
      setLiveEvents(eventsRes.events || []);

      if (intList.length > 0) startPolling();
    } catch (error) {
      console.error('[AccessControl] Failed to load data:', error);
      setNoIntegrations(true);
    } finally {
      setLoading(false);
    }
  }, [tenantVersion]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const [doorsRes, eventsRes] = await Promise.all([
          fetchWithTenant('/api/acs/doors').then(r => r.json()),
          fetchWithTenant('/api/acs/events?count=50').then(r => r.json()).catch(() => ({ events: [] })),
        ]);
        setDoors((doorsRes.doors || []).map(normalizeDoor));
        setFetchErrors(doorsRes.errors || []);
        setLiveEvents(eventsRes.events || []);
      } catch (error) {
        console.error('[AccessControl] Polling error:', error);
      }
    }, 8000);
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // ===========================
  // Door Control (unified ACS endpoints)
  // ===========================

  const handleUnlockDoor = async (door) => {
    try {
      const result = await fetchWithTenant(
        `/api/acs/doors/${door.integrationId}/${encodeURIComponent(door.id)}/unlock`,
        { method: 'POST' }
      ).then(r => r.json());
      if (result.success) {
        toast.success(`Door unlocked: ${door.name}`);
      } else {
        toast.error(`Failed to unlock: ${result.error || result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Failed to unlock: ${error.message}`);
    }
  };

  const handleHoldDoor = async (door) => {
    try {
      const result = await fetchWithTenant(
        `/api/acs/doors/${door.integrationId}/${encodeURIComponent(door.id)}/hold`,
        { method: 'POST' }
      ).then(r => r.json());
      if (result.success) {
        toast.success(`Door held open: ${door.name}`);
        loadData();
      } else {
        toast.error(`Failed to hold door: ${result.error || result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Failed to hold door: ${error.message}`);
    }
  };

  const handleLockDoor = async (door) => {
    try {
      const result = await fetchWithTenant(
        `/api/acs/doors/${door.integrationId}/${encodeURIComponent(door.id)}/lock`,
        { method: 'POST' }
      ).then(r => r.json());
      if (result.success) {
        toast.success(`Door locked: ${door.name}`);
        loadData();
      } else {
        toast.error(`Failed to lock: ${result.error || result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Failed to lock: ${error.message}`);
    }
  };

  // ===========================
  // Lockdown (unified ACS endpoints)
  // ===========================

  const handleLockdownClick = () => {
    setShowLockdownConfirm(true);
    setLockdownStep(1);
  };

  const handleLockdownConfirm = async () => {
    if (lockdownStep === 1) {
      setLockdownStep(2);
      return;
    }
    try {
      const result = await fetchWithTenant('/api/acs/emergency/lockdown', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Emergency lockdown activated from UI' }),
      }).then(r => r.json());
      if (result.lockdownActive) {
        setLockdownActive(true);
        toast.success('Emergency lockdown activated across all access control systems');
        loadData();
      } else {
        toast.error('Lockdown failed');
      }
    } catch (error) {
      toast.error(`Lockdown failed: ${error.message}`);
    }
    setShowLockdownConfirm(false);
    setLockdownStep(0);
  };

  const handleCancelLockdown = async () => {
    try {
      const result = await fetchWithTenant('/api/acs/emergency/cancel', {
        method: 'POST',
      }).then(r => r.json());
      setLockdownActive(false);
      toast.success('Lockdown deactivated');
      loadData();
    } catch (error) {
      toast.error(`Failed to cancel lockdown: ${error.message}`);
    }
  };

  // ===========================
  // Filtering
  // ===========================

  const filteredDoors = doors.filter(d => {
    if (doorFilter && d.id !== doorFilter) return false;
    if (integrationFilter && d.integrationId !== integrationFilter) return false;
    return true;
  }).sort((a, b) => {
    if (a.isHeldOpen && !b.isHeldOpen) return -1;
    if (!a.isHeldOpen && b.isHeldOpen) return 1;
    return 0;
  });

  const filteredEvents = liveEvents.filter(e => {
    if (integrationFilter && e.integrationId !== integrationFilter) return false;
    return true;
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  // ===========================
  // Render
  // ===========================

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading access control...</p>
        </div>
      </div>
    );
  }

  if (noIntegrations) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Access Control</h1>
        </div>
        <div className="card">
          <div className="empty-state">
            <Shield size={48} />
            <h3>No Access Control Configured</h3>
            <p>Add an access control integration (Feenics, Genetec, Axis) in Admin Settings.</p>
            <a href="/admin" className="btn btn-primary config-link">
              <Settings size={16} /> Go to Admin Settings
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md-page access-control-page">
      <CcmLicenseBanner />
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Shield size={22} /> Access Control
            {ccmReadOnly && <span className="ccm-readonly-badge">Read Only</span>}
          </h1>
          <p className="page-subtitle">
            {integrations.length} integration{integrations.length !== 1 ? 's' : ''} &middot; {doors.length} door{doors.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="page-header-actions">
          <button className="tutorial-help-btn" onClick={() => startTutorial(accessControlTutorial.module, accessControlTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {/* Integration errors banner */}
      {fetchErrors.length > 0 && (
        <div className="acs-error-banner">
          <AlertTriangle size={16} />
          <span>
            {fetchErrors.map(e => `${e.integrationName}: ${e.error}`).join(' | ')}
          </span>
        </div>
      )}

      {/* Emergency Lockdown Bar */}
      <div className={`lockdown-bar ${lockdownActive ? 'active' : ''}`}>
        {lockdownActive ? (
          <>
            <div className="lockdown-status">
              <Lock size={20} />
              <span>EMERGENCY LOCKDOWN ACTIVE</span>
            </div>
            <button className="btn btn-danger" onClick={handleCancelLockdown}>
              <Unlock size={16} /> Cancel Lockdown
            </button>
          </>
        ) : (
          <>
            <div className="lockdown-status">
              <Shield size={20} />
              <span>System Normal</span>
            </div>
            <button className="btn btn-danger" onClick={handleLockdownClick}>
              <Lock size={16} /> Emergency Lockdown
            </button>
          </>
        )}
      </div>

      {/* Tabs + Filters */}
      <div className="access-tabs">
        <button
          className={`btn ${activeTab === 'readers' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('readers')}
        >
          <Radio size={16} /> Doors ({filteredDoors.length})
        </button>
        <button
          className={`btn ${activeTab === 'live' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('live')}
        >
          <Activity size={16} /> Live Events
        </button>
        <button
          className={`btn ${activeTab === 'history' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('history')}
        >
          <History size={16} /> History
        </button>

        <div className="access-filter-group" style={{ marginLeft: 'auto' }}>
          {integrations.length > 1 && (
            <select
              value={integrationFilter}
              onChange={(e) => setIntegrationFilter(e.target.value)}
              className="form-select"
              style={{ minWidth: '140px' }}
            >
              <option value="">All Systems</option>
              {integrations.map((integ) => (
                <option key={integ.id} value={integ.id}>{integ.name}</option>
              ))}
            </select>
          )}
          <Filter size={14} />
          <select
            value={doorFilter}
            onChange={(e) => setDoorFilter(e.target.value)}
            className="form-select"
          >
            <option value="">All Doors</option>
            {doors.map((d) => (
              <option key={`${d.integrationId}-${d.id}`} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-secondary"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={16} className={refreshing ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* ========== DOORS TAB ========== */}
        {activeTab === 'readers' && (
          <div className="readers-grid">
            {filteredDoors.length === 0 ? (
              <div className="card">
                <div className="empty-state">
                  <Radio size={24} />
                  <p>No doors found</p>
                </div>
              </div>
            ) : (
              filteredDoors.map((door) => (
                <div
                  key={`${door.integrationId}-${door.id}`}
                  className={`card reader-card ${door.isHeldOpen ? 'held-open' : ''}`}
                >
                  <div className="reader-header">
                    <span className="reader-mode" style={{ color: getModeColor(door.displayStatus) }}>
                      {door.isHeldOpen || door.displayStatus === 'open' ? (
                        <DoorOpen size={16} />
                      ) : (
                        <DoorClosed size={16} />
                      )}
                      {getModeLabel(door)}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {door.isHeldOpen && (
                        <span className="reader-status-held">Held Open</span>
                      )}
                      {door.isForcedOpen && (
                        <span className="reader-status-forced">Forced Open</span>
                      )}
                    </div>
                  </div>
                  <div className="reader-info">
                    <h4>{door.name}</h4>
                    {door.controllerName && (
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{door.controllerName}</p>
                    )}
                  </div>
                  <div className="reader-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleUnlockDoor(door)}
                      title="Momentary unlock"
                    >
                      <Unlock size={14} /> Pulse
                    </button>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleHoldDoor(door)}
                      title="Hold door open until locked"
                    >
                      <LockOpen size={14} /> Hold Open
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleLockDoor(door)}
                      title="Lock door"
                    >
                      <Lock size={14} /> Lock
                    </button>
                  </div>
                  <VendorBadge
                    integrationType={door.integrationType}
                    vendorLogo={door.vendorLogo}
                  />
                </div>
              ))
            )}
          </div>
        )}

        {/* ========== LIVE EVENTS TAB ========== */}
        {activeTab === 'live' && (
          <div className="card">
            <div className="events-header">
              <h3 className="events-title">
                <Activity size={18} /> Live Events
                <span className="events-count">({filteredEvents.length} events)</span>
              </h3>
            </div>
            {filteredEvents.length === 0 ? (
              <div className="empty-state">
                <Activity size={24} />
                <p>No recent events</p>
              </div>
            ) : (
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Door</th>
                      <th>Event</th>
                      <th>Person</th>
                      {integrations.length > 1 && <th>System</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((event, index) => (
                      <tr key={event.id || index}>
                        <td className="time-cell" data-label="Time">
                          <Clock size={12} />
                          {formatTimestamp(event.timestamp)}
                        </td>
                        <td data-label="Door">
                          {event.doorName || '-'}
                        </td>
                        <td data-label="Event">
                          {event.eventType || event.type || event.message || '-'}
                        </td>
                        <td data-label="Person">
                          {event.personName || event.cardholderName || '-'}
                        </td>
                        {integrations.length > 1 && (
                          <td data-label="System">
                            <VendorBadge
                              integrationType={event.integrationType}
                              vendorLogo={event.vendorLogo}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ========== HISTORY TAB ========== */}
        {activeTab === 'history' && (
          <div className="card">
            <div className="empty-state">
              <History size={48} />
              <h3>Event History</h3>
              <p>Coming Soon</p>
              <p className="text-muted coming-soon-hint">
                Search and export historical access events
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Lockdown Confirmation Modal */}
      {showLockdownConfirm && (
        <div
          className="lockdown-modal-overlay"
          onClick={() => { setShowLockdownConfirm(false); setLockdownStep(0); }}
        >
          <div className="lockdown-modal card" onClick={(e) => e.stopPropagation()}>
            <div className="lockdown-modal-header">
              <AlertTriangle size={48} />
              <h3>Emergency Lockdown</h3>
            </div>
            <div className="lockdown-modal-body">
              {lockdownStep === 1 && (
                <>
                  <p>You are about to initiate an <strong>Emergency Lockdown</strong>.</p>
                  <p>
                    This will immediately lock all doors across{' '}
                    <strong>{integrations.length} access control system{integrations.length !== 1 ? 's' : ''}</strong>.
                  </p>
                </>
              )}
              {lockdownStep === 2 && (
                <div className="lockdown-modal-body confirm-step">
                  <AlertTriangle size={32} className="text-danger" />
                  <p className="lockdown-warning">FINAL CONFIRMATION</p>
                  <p>Press <strong>CONFIRM LOCKDOWN</strong> to lock all doors immediately.</p>
                </div>
              )}
            </div>
            <div className="lockdown-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowLockdownConfirm(false); setLockdownStep(0); }}
              >
                Cancel
              </button>
              <button
                className={`btn ${lockdownStep === 2 ? 'btn-danger' : 'btn-warning'}`}
                onClick={handleLockdownConfirm}
              >
                <Lock size={16} />
                {lockdownStep === 1 ? 'Continue' : 'CONFIRM LOCKDOWN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={accessControlTutorial.module}
          moduleName={accessControlTutorial.moduleName}
          steps={accessControlTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default AccessControl;
